import type { UseQueryResult } from '@tanstack/react-query'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { COMMUNITY_SECURE_GLOBAL_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseQueryClearOptions } from 'shared/types/tanstack-utility-types'
import { addQueryParam } from 'shared/utils/add-query-param'
import type {
  InvitationListResponse,
  InvitationStatus,
} from 'modules/community/types/invitation-interface'
import { communityKeys } from './key-factory'

const INVITATION_LIMIT = 9

interface UseInvitationsOptions extends UseQueryClearOptions<
  InvitationListResponse,
  Error,
  InvitationListResponse
> {
  communityPath: string
  status: InvitationStatus
  page: number
  searchBy: string
}

export const useInvitations = ({
  communityPath,
  status,
  page,
  searchBy,
  ...options
}: UseInvitationsOptions): UseQueryResult<InvitationListResponse, Error> => {
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    InvitationListResponse
  >({
    method: RequestMethodsEnum.get,
  })

  return useQuery(
    queryOptions({
      queryKey: communityKeys.invitations.list(communityPath, status, page, searchBy),
      queryFn: () => {
        const search = new URLSearchParams()
        addQueryParam(search, 'pagination[limit]', INVITATION_LIMIT)
        addQueryParam(search, 'pagination[page]', page)
        addQueryParam(search, 'filter[searchBy]', searchBy)
        return fetcher(
          `${COMMUNITY_SECURE_GLOBAL_PREFIX}/${communityPath}/invitations/${status}?${search}`,
        )
      },
      enabled: Boolean(communityPath),
      ...options,
    }),
  )
}
